<template>
  <div class="flex flex-col p-3 w-full" data-testid="aki-score-item">
    <div class="flex items-center gap-2">
      <VTooltip
        :text="t('Acute Kidney Injury Prediction Service')"
        :location="'bottom start'"
        :open-delay="500"
      >
        <template #activator="{ props: activatorProps }">
          <span v-bind="activatorProps" class="text-h6 text-[#17485D] cursor-default">
            {{ t('AKI Prediction Service') }}
          </span>
        </template>
      </VTooltip>
      <VChip
        :style="{ '--v-border-opacity': '1', '--v-activated-opacity': '0' }"
        :text="t('AI-Generated Output')"
        :density="'compact'"
        :size="20"
        class="bg-utility-blue-50 text-utility-blue-700 border-utility-blue-200 border-sm font-semibold text-xs tracking-normal px-2 w-fit"
      />
    </div>
    <span
      class="text-body-2"
      :class="isAkiError ? 'text-[#FF9900] font-medium' : 'text-[#206683]'"
      data-testid="aki-score-timestamp"
    >
      {{ resultError ? akiErrorLabel : akiTimestamp }}
    </span>

    <div class="p-4 bg-[#F3F4F7] rounded-md mt-2 flex justify-center items-center">
      <span
        class="value"
        :class="isAkiError ? '!text-base font-semibold' : ''"
        :style="akiRiskLabelStyle"
        data-testid="aki-score-value"
      >
        {{ akiRiskLabel }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type ErrorResponseDto,
  ErrorResponseDtoCodeEnum,
  type PredictionResultDTO,
  PredictionResultDTOProbabilityColorCodeEnum,
  PredictionResultDTOProbabilityEnum
} from '@/generated'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatTimestamp } from '../molecules/VitalInfoTable.utils'

interface Props {
  akiScore?: PredictionResultDTO | ErrorResponseDto | null
}

const props = defineProps<Props>()
const { t } = useI18n()
const DASH = '–'

const resultError = computed((): ErrorResponseDto | null => {
  const score = props.akiScore
  if (!score) return null

  const isErr =
    ('status' in score && score.status === 422) ||
    'errorCode' in score ||
    !('probability' in score) ||
    !('probabilityColorCode' in score) ||
    !('probabilityTimestamp' in score)

  return isErr ? (score as ErrorResponseDto) : null
})

const resultSuccess = computed((): PredictionResultDTO | null => {
  const score = props.akiScore
  if (!score || isAkiError.value) return null
  return score as PredictionResultDTO
})

const isAkiError = computed(() => props.akiScore == null || !!resultError.value)

const akiTimestamp = computed(() => {
  const ts = resultSuccess.value?.probabilityTimestamp
  return ts ? formatTimestamp(ts) : DASH
})

const akiErrorLabel = computed(() => {
  const err = resultError.value
  if (!err) return DASH

  switch (err.code) {
    case ErrorResponseDtoCodeEnum.NOT_FOUND:
    case ErrorResponseDtoCodeEnum.NO_AVAILABLE_PHYSICIAN:
      return t('An error occurred')
  }

  if (err.code === ErrorResponseDtoCodeEnum.VALIDATION_FAILED && err.detail) {
    return `${t('Missing parameter')}: ${err.detail.split(' ').join(', ')}`
  }

  return err.detail || t('Data unavailable')
})

const akiRiskLabel = computed(() => {
  if (isAkiError.value) {
    return DASH
  }
  switch (resultSuccess.value?.probability) {
    case PredictionResultDTOProbabilityEnum.VERY_HIGH:
      return t('Very High Risk')
    case PredictionResultDTOProbabilityEnum.HIGH:
      return t('High Risk')
    case PredictionResultDTOProbabilityEnum.MEDIUM_HIGH:
      return DASH
    default:
      return DASH
  }
})

const akiRiskLabelStyle = computed(() => {
  if (isAkiError.value) {
    return { color: PredictionResultDTOProbabilityColorCodeEnum.FF9900 }
  }
  switch (resultSuccess.value?.probabilityColorCode) {
    case PredictionResultDTOProbabilityColorCodeEnum.FF9900:
      return { color: PredictionResultDTOProbabilityColorCodeEnum.FF9900 }
    case PredictionResultDTOProbabilityColorCodeEnum.E71010:
      return { color: PredictionResultDTOProbabilityColorCodeEnum.E71010 }
    default:
      return { color: '#17485D' }
  }
})
</script>

<style scoped>
.value {
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}
</style>
